/**
 * 趋势折线图子组件
 */
import React, { useEffect, useMemo } from 'react';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V } from '@op/utils/format';
import { chartAllColors } from '@op/constants';
import type { ITrendLineChartProps } from './types';
import { formatTimeXAxisText } from '@op/utils/chart';

const TrendLineChart: React.FC<ITrendLineChartProps> = ({
  data,
  compareData,
  metric,
  metricLabel,
  chartType,
  height = 300,
  showCompare = false,
  crossAnalysisData,
  crossAnalysisLabel,
  yAxisFormat = 'integer',
  filter,
}) => {
  // ==================== Y轴格式化 ====================
  const yAxisFormatter = useMemo(() => {
    if (yAxisFormat === 'largeNumber') {
      return (value: number) => DBO_EC.reviews(value);
    }
    return (value: number) => DBO_EC.reviews(value);
  }, [yAxisFormat]);

  // ==================== 图表配置 ====================
  const chartOption = useMemo(() => {
    if (!data?.series?.length || data?.isEmpty) return null;

    const xAxisData = data.xAxis || [];
    const series: any[] = [];

    // 主系列 - 根据图表类型配置
    const mainLegend: string[] = [];
    data.series.forEach((item) => {
      const name = metricLabel || item.name;
      mainLegend.push(name);
      const seriesItem: any = {
        name,
        type: chartType === 'bar' ? 'bar' : 'line',
        data: item.data.map((v: number) => v || undefined),
        smooth: false,
        stack: chartType === 'bar' ? 'total' : undefined,
        emphasis: {
          focus: 'series',
        },
      };

      series.push(seriesItem);
    });

    // 对比周期系列（虚线）
    if (showCompare && compareData?.series?.length) {
      compareData.series.forEach((item) => {
        series.push({
          name: `${item.name}_compare`,
          type: 'line',
          data: item.data.map((v: number) => v || undefined),
          lineStyle: {
            type: 'dashed',
          },
          itemStyle: {
            opacity: 0.6,
          },
        });
      });
    }

    // 交叉分析系列（右轴）
    const crossAnalysisLegend: string[] = [];
    if (crossAnalysisData?.series?.length) {
      crossAnalysisData.series.forEach((item) => {
        const name = crossAnalysisLabel || item.name;
        crossAnalysisLegend.push(name);
        series.push({
          name,
          type: 'line',
          yAxisIndex: 1,
          data: item.data.map((v: number) => v || undefined),
        });
      });
    }

    // 合并图例：主指标 + 交叉分析指标
    const legendData = [...mainLegend, ...crossAnalysisLegend];
    return {
      color: chartAllColors,
      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: (params: any[]) => {
          return formatChartParamsDate({
            title:
              DBO_EC.toolTime(params[0]?.name, filter?.date_type || 'daily') ||
              '',
            params,
            valueFormat: (value: number) => DBO_V.reviews(value),
          });
        },
      },
      legend: {
        bottom: 0,
        data: legendData,
      },
      grid: {
        right: 20,
        top: 20,
      },
      axisPointer: DBO_EC.axisPointer((data: any) =>
        DBO_EC.timeX(data.value, filter?.date_type || 'daily'),
      ),
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          formatter: (value: string) =>
            DBO_EC.timeX(value, filter?.date_type || 'daily'),
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: yAxisFormatter,
          },
        },
        crossAnalysisData
          ? {
            type: 'value',
            position: 'right',
            axisLabel: {
              formatter: yAxisFormatter,
            },
          }
          : undefined,
      ].filter(Boolean),
      series,
    };
  }, [
    data,
    compareData,
    chartType,
    showCompare,
    crossAnalysisData,
    crossAnalysisLabel,
    yAxisFormatter,
    metricLabel,
  ]);

  useEffect(() => {
    console.log('切换');
  }, [data, compareData]);

  // ==================== 渲染逻辑 ====================
  return (
    <div className="dbo-trend-line-chart">
      <EChartCommon
        options={chartOption || {}}
        height={height}
        optionsProps={{ type: chartType }}
        empty={!data?.series?.length || data?.isEmpty}
      />
    </div>
  );
};

export default React.memo(TrendLineChart);
